import { AppConfiguration } from "config"
import { bsh_getAdminUserDetails, bsh_getClientUserDetails } from "helpers/sf_browser_storage_helper"
import { responseErrorChecker } from "helpers/sf_utils"
import { sf_fetch } from "helpers/sf_fetch"
import { format } from "date-fns"
import { utcToZonedTime } from "date-fns-tz"
import {
  INTEGRATION_LEAD_TYPES,
  PRODUCT_TYPE_IDS,
  REPORTING_TIMEZONE,
  REQUIRED_FEATURE_FLAGS,
  LEAD_MARKETPLACE_PRODUCT_LABEL,
} from "helpers/sf_constants"
import { us_states } from "common/data/us_states"
import { canUseAdminAccountStats } from "helpers/admin_analytics_optimization_helper"

const REPORTING_PRODUCT_CONFIGS = {
  [PRODUCT_TYPE_IDS.CLICKS]: {
    label: "Clicks",
    segment: "clicks",
    routing: {
      campaignStats: {
        newCampaignVersion: "v2",
        accountFlag: REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_USE_V3_API_FOR_CAMPAIGN_STATS,
        accountFlagVersion: "v3",
        fallbackVersion: "v1",
      },
      adminAnalytics: {
        revamp: true,
        flag: REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_ADMIN_CLICKS_API_VERSION,
        flagVersion: "v2",
        fallbackVersion: "v1",
      },
      dashboard: {
        accountFlag: REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_USE_DASHBOARD_V3_API,
        accountFlagVersion: "v3",
        fallbackVersion: "v1",
      },
    },
    analyticsPath: "/api/v1/analytics/clicks",
    campaignStatsPathByVersion: {
      v1: "/api/v1/click_campaign_stats",
      v2: "/api/v2/click_campaign_stats",
      v3: "/api/v3/click_campaign_stats",
    },
    dashboardPathByVersion: {
      v1: "/api/v1/dashboards",
      v3: "/api/v3/dashboards",
    },
    metrics: {
      searches: ["searches", "total_searches", "click_analytic_searches", "click_analytic_total_searches"],
      impressions: ["impressions", "click_analytic_impressions"],
      clicks: ["clicks", "total_clicks", "click_analytic_clicks", "click_analytic_total_clicks"],
      // Preserve the original checker contract when older endpoints expose
      // these fields. Newer endpoints may omit them, so they remain optional.
      successes: ["successes", "total_successes", "click_analytic_successes"],
      cost: ["cost", "spend", "total_cost", "click_analytic_total_cost", "click_analytic_total_payout"],
      total_bid: ["total_bid", "click_analytic_total_bid"],
    },
  },
  [PRODUCT_TYPE_IDS.CALLS]: {
    label: "Calls",
    segment: "calls",
    routing: {
      campaignStats: {
        newCampaignVersion: "v2",
        fallbackVersion: "v1",
      },
      adminAnalytics: {
        revamp: true,
        flag: REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_ADMIN_CALLS_API_VERSION,
        flagVersion: "v2",
        fallbackVersion: "v1",
      },
      dashboard: {
        fallbackVersion: "v1",
      },
    },
    analyticsPath: "/api/v1/analytics/calls",
    campaignStatsPathByVersion: {
      v1: "/api/v1/call_campaign_stats",
      v2: "/api/v2/call_campaign_stats",
    },
    dashboardPathByVersion: {
      v1: "/api/v1/dashboard/calls/fetch_main_data",
    },
    metrics: {
      searches: ["searches", "total_searches", "call_analytic_searches", "call_analytic_total_searches"],
      calls: ["calls", "total_calls", "call_analytic_total_calls"],
      successes: ["successes", "total_successes", "call_analytic_successes"],
      cost: ["cost", "spend", "total_cost", "call_analytic_total_cost"],
      total_bid: ["total_bid", "call_analytic_total_bid"],
    },
  },
  [PRODUCT_TYPE_IDS.LEADS]: {
    label: "Leads",
    segment: "leads",
    routing: {
      campaignStats: {
        newCampaignVersion: "v2",
        fallbackVersion: "v1",
      },
      adminAnalytics: {
        revamp: true,
        fallbackVersion: "v1",
      },
      dashboard: {
        fallbackVersion: "v1",
      },
    },
    analyticsPath: "/api/v1/analytics/leads",
    campaignStatsPathByVersion: {
      v1: "/api/v1/lead_campaign_stats",
      v2: "/api/v2/lead_campaign_stats",
    },
    dashboardPathByVersion: {
      v1: "/api/v1/dashboard/leads/fetch_main_data",
    },
    metrics: {
      searches: ["searches", "total_searches", "lead_analytic_searches", "lead_analytic_total_searches"],
      leads: ["leads", "total_leads", "lead_analytic_total_leads"],
      successes: ["successes", "total_successes", "lead_analytic_successes"],
      cost: ["cost", "spend", "total_cost", "lead_analytic_total_cost"],
      total_bid: ["total_bid", "lead_analytic_total_bid"],
    },
  },
  [PRODUCT_TYPE_IDS.QUOTE_FUNNELS]: {
    label: "Quote Funnels",
    segment: "quote_funnels",
    routing: {
      campaignStats: {
        featureFlag: REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_QF_CAMPAIGNS_AGGREGATED,
        featureFlagVersion: "v4",
        fallbackVersion: "v2",
      },
      adminAnalytics: {
        revamp: false,
        fallbackVersion: "v2",
      },
      dashboard: {
        featureFlag: REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_DASHBOARD_STATS_V4,
        featureFlagVersion: "v4",
        fallbackVersion: "v1",
      },
      childStats: {
        adGroupFeatureFlag: REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_AD_GROUP_STATS_V4,
        sourceFeatureFlag: REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_USE_QF_SOURCE_STATS_STORED_PROCEDURES,
      },
    },
    analyticsPath: "/api/v1/analytics/clicks",
    campaignStatsPathByVersion: {
      v2: "/api/v2/click_campaign_stats",
      v4: "/api/v4/campaign_stats/quote_funnels",
    },
    dashboardPathByVersion: {
      v1: "/api/v1/dashboards",
      v4: "/api/v4/dashboard_stats/quote_funnels",
    },
    metrics: {
      searches: ["total_searches", "total_bids", "click_analytic_searches", "click_analytic_total_searches"],
      impressions: ["total_selected", "total_impressions", "click_analytic_impressions"],
      clicks: ["total_accepted", "total_clicks", "click_analytic_clicks", "click_analytic_total_clicks"],
      cost: ["total_cost", "total_spend", "cost", "click_analytic_total_cost", "click_analytic_total_payout"],
      leads: ["leads_generated_count", "total_leads", "lead_analytic_total_leads"],
      calls: ["calls_generated_count", "total_calls", "call_analytic_total_calls"],
    },
  },
}

const GOAL_LEAD_MARKETPLACE_CONFIG = {
  ...REPORTING_PRODUCT_CONFIGS[PRODUCT_TYPE_IDS.LEADS],
  label: LEAD_MARKETPLACE_PRODUCT_LABEL,
  routing: {
    ...REPORTING_PRODUCT_CONFIGS[PRODUCT_TYPE_IDS.LEADS].routing,
    // Goal Marketplace is already served by the V4 aggregate contracts. The
    // legacy Leads flags must not route this product back to V1/V2.
    campaignStats: { fallbackVersion: "v4" },
    dashboard: { fallbackVersion: "v4" },
    childStats: { forceV4: true },
  },
  campaignStatsPathByVersion: {
    ...REPORTING_PRODUCT_CONFIGS[PRODUCT_TYPE_IDS.LEADS].campaignStatsPathByVersion,
    v4: "/api/v4/campaign_stats/leads",
  },
  dashboardPathByVersion: {
    ...REPORTING_PRODUCT_CONFIGS[PRODUCT_TYPE_IDS.LEADS].dashboardPathByVersion,
    v4: "/api/v4/dashboard_stats/leads_main_data",
  },
  metrics: {
    inventory: ["lead_analytic_total_inventory", "total_inventory"],
    opportunities: ["lead_analytic_total_opportunities", "total_opportunities"],
    searches: ["lead_analytic_total_searches", "lead_analytic_searches", "total_searches", "searches"],
    won: ["lead_analytic_total_won", "total_won", "lead_analytic_total_selected", "total_selected"],
    leads: ["lead_analytic_total_leads", "total_leads", "leads"],
    cost: ["lead_analytic_total_cost", "total_cost", "cost", "spend"],
    avg_bid: ["lead_analytic_avg_bid", "avg_bid"],
    avg_cpl: ["lead_analytic_avg_cpl", "avg_cpl"],
    target_rate: ["lead_analytic_target_rate", "target_rate"],
    bid_rate: ["lead_analytic_bid_rate", "bid_rate"],
    win_rate: ["lead_analytic_win_rate", "win_rate"],
    success_rate: ["lead_analytic_success_rate", "success_rate"],
  },
  aggregationMetrics: ["inventory", "opportunities", "searches", "won", "leads", "cost"],
}

// Admin Analytics v3 intentionally returns product-neutral funnel counters.
// Keep this mapping separate from product-specific API aliases so a generic
// field is never interpreted as a product metric on the wrong source.
const ADMIN_ANALYTICS_V3_METRICS = {
  [PRODUCT_TYPE_IDS.CLICKS]: {
    searches: ["total_searches"],
    impressions: ["total_selected"],
    clicks: ["total_accepted"],
    cost: ["total_cost"],
  },
  [PRODUCT_TYPE_IDS.CALLS]: {
    searches: ["total_searches"],
    calls: ["total_selected"],
    billable_calls: ["total_accepted"],
    cost: ["total_cost"],
  },
  [PRODUCT_TYPE_IDS.LEADS]: {
    searches: ["total_searches"],
    leads: ["total_accepted"],
    cost: ["total_cost"],
  },
}

// Reporting surfaces do not expose the same metric set. Keep the aliases
// source-specific so an omitted, unsupported field is not reported as a
// mismatch. In particular, the normal Portal Calls response has outcomes and
// cost but no searches; the stored-procedure Dashboard responses expose only
// their rollup metrics.
const REPORTING_SOURCE_METRICS = {
  [PRODUCT_TYPE_IDS.CALLS]: {
    portal: {
      calls: ["calls", "total_calls", "call_analytic_total_calls"],
      billable_calls: ["billable_calls", "total_billable_calls", "call_analytic_billable_calls"],
      cost: ["cost", "spend", "total_cost", "call_analytic_total_cost"],
      total_duration: ["total_duration", "call_analytic_total_duration", "call_analytic_total_call_duration"],
    },
    campaign_stats: {
      calls: ["calls", "total_calls", "call_analytic_total_calls"],
      billable_calls: ["billable_calls", "total_billable_calls", "call_analytic_billable_calls"],
      cost: ["cost", "spend", "total_cost", "call_analytic_total_cost"],
      total_duration: ["total_duration", "call_analytic_total_duration", "call_analytic_total_call_duration"],
    },
    dashboard: {
      billable_calls: ["billable_calls", "total_billable_calls", "call_analytic_billable_calls"],
      // Calls Dashboard reads out_of_pocket_amt, while Admin/Campaign Stats
      // cost is total_spend. They are intentionally not compared as one field.
      total_duration: ["total_duration", "call_analytic_total_duration", "call_analytic_total_call_duration"],
    },
  },
  [PRODUCT_TYPE_IDS.LEADS]: {
    portal: {
      searches: ["searches", "total_searches", "lead_analytic_searches", "lead_analytic_total_searches"],
      leads: ["leads", "total_leads", "lead_analytic_total_leads"],
      cost: ["cost", "spend", "total_cost", "lead_analytic_total_cost"],
    },
    campaign_stats: {
      searches: ["searches", "total_searches", "lead_analytic_searches", "lead_analytic_total_searches"],
      leads: ["leads", "total_leads", "lead_analytic_total_leads"],
      cost: ["cost", "spend", "total_cost", "lead_analytic_total_cost"],
    },
    dashboard: {
      leads: ["leads", "total_leads", "lead_analytic_total_leads"],
      cost: ["cost", "spend", "total_cost", "lead_analytic_total_cost"],
    },
  },
}

const GOAL_ADMIN_ANALYTICS_V3_METRICS = {
  inventory: ["total_inventory"],
  opportunities: ["total_opportunities"],
  searches: ["total_searches"],
  won: ["total_selected"],
  leads: ["total_accepted"],
  cost: ["total_cost"],
}

// The legacy Goal Portal Analytics and V4 Dashboard main-data contracts do
// not expose the full Marketplace funnel. Comparisons must use the metrics
// each surface actually supports; a missing field on one contract is not a
// data mismatch by itself.
const GOAL_SOURCE_METRICS = {
  admin_v3: Object.keys(GOAL_ADMIN_ANALYTICS_V3_METRICS),
  portal_analytics: ["searches", "won", "leads", "cost", "avg_bid", "avg_cpl", "win_rate"],
  dashboard: ["opportunities", "searches", "won", "leads", "cost", "avg_bid", "avg_cpl"],
}

const GENERIC_REPORTING_PRODUCT_OPTIONS = Object.entries(REPORTING_PRODUCT_CONFIGS)
  .filter(([value]) => Number(value) !== PRODUCT_TYPE_IDS.QUOTE_FUNNELS)
  .map(([value, config]) => ({
  value: Number(value),
  label: config.label,
}))

export const REPORTING_PRODUCT_OPTIONS = GENERIC_REPORTING_PRODUCT_OPTIONS

export const getReportingProductOptions = ({ isGoalBrand = false, isLeadMarketplaceEnabled = false } = {}) => {
  if (!isGoalBrand) return GENERIC_REPORTING_PRODUCT_OPTIONS

  const options = [{ value: PRODUCT_TYPE_IDS.QUOTE_FUNNELS, label: REPORTING_PRODUCT_CONFIGS[PRODUCT_TYPE_IDS.QUOTE_FUNNELS].label }]
  if (isLeadMarketplaceEnabled) {
    options.push({ value: PRODUCT_TYPE_IDS.LEADS, label: GOAL_LEAD_MARKETPLACE_CONFIG.label })
  }
  return options
}

export const REPORTING_COMPARISON_MODES = {
  ALL_SURFACES: "all_surfaces",
  ADMIN_PORTAL: "admin_portal",
}

export const REPORTING_COMPARISON_OPTIONS = [
  {
    value: REPORTING_COMPARISON_MODES.ALL_SURFACES,
    label: "All surfaces (Admin, Portal, Campaigns and Dashboard)",
  },
  {
    value: REPORTING_COMPARISON_MODES.ADMIN_PORTAL,
    label: "Admin Analytics vs Portal Analytics",
  },
]

const SHARED_GROUP_BY_OPTIONS = [
  { label: "Brands", value: "brand_id" },
  { label: "Campaigns", value: "campaign_id" },
  { label: "Ad groups", value: "ad_group_id" },
  { label: "Day of week", value: "day_of_week", featureFlag: "SF_FEATURE_FLAG_DOW_GROUP_BY_ANALYTICS" },
  { label: "Hour", value: "hour" },
  { label: "Date", value: "start_of_day" },
  { label: "Week", value: "start_of_week" },
  { label: "Month", value: "start_of_month" },
  { label: "Quarter", value: "start_of_quarter" },
  { label: "Year", value: "start_of_year" },
  { label: "Marketing groups", value: "marketing_group", featureFlag: "SF_FEATURE_FLAG_MARKETING_GROUP_ANALYTICS" },
  { label: "States", value: "state" },
  {
    label: "Zip code",
    value: "zip",
    featureFlags: ["SF_FEATURE_FLAG_ADMIN_ANALYTICS_ZIPCODE_GROUP_BY"],
    featureFlagsByProduct: {
      [PRODUCT_TYPE_IDS.CLICKS]: ["SF_FEATURE_FLAG_ENABLE_ZIP_ZODE_GROUP_BY_FOR_CLICKS_IN_ANALYTICS"],
      [PRODUCT_TYPE_IDS.CALLS]: ["SF_FEATURE_FLAG_ENABLE_ZIP_ZODE_GROUP_BY_FOR_CALLS_IN_ANALYTICS"],
      [PRODUCT_TYPE_IDS.LEADS]: ["SF_FEATURE_FLAG_ENABLE_ZIP_ZODE_GROUP_BY_FOR_LEADS_IN_ANALYTICS"],
    },
  },
  { label: "Insurance types", value: "lead_type_id" },
  { label: "Device types", value: "device_type", products: [PRODUCT_TYPE_IDS.CLICKS, PRODUCT_TYPE_IDS.LEADS] },
  { label: "Source types", value: "source_type_id" },
  { label: "Age", value: "consumer_age" },
  { label: "Currently insured", value: "currently_insured" },
  { label: "Home owner", value: "home_owner" },
  { label: "Continuous coverage", value: "continuous_coverage" },
  { label: "Gender", value: "gender" },
  { label: "Marital status", value: "marital_status" },
  { label: "Education", value: "education" },
  { label: "Credit rating", value: "credit_rating" },
  { label: "Military affiliation", value: "military_affiliation" },
  { label: "Number of drivers", value: "num_drivers" },
  { label: "Number of vehicles", value: "num_vehicles" },
  { label: "License status", value: "license_status" },
]

export const REPORTING_CONSISTENCY_FILTER_OPTIONS = [
  { label: "Brand IDs", key: "brand_ids", numeric: true },
  { label: "Campaign IDs", key: "campaign_ids", numeric: true },
  { label: "Insurance type IDs", key: "lead_type_ids", numeric: true },
]

export const getSupportedFilterOptions = ({
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  isFeatureFlagActive = () => false,
} = {}) => REPORTING_CONSISTENCY_FILTER_OPTIONS.filter(option => (
  (!option.products || option.products.includes(Number(productTypeId)))
  && (!option.featureFlag || isFeatureFlagActive(option.featureFlag))
))

const uniqueFilterValueOptions = options => Object.values(options.reduce((unique, option) => {
  if (option.value !== null && option.value !== undefined && option.value !== "") {
    unique[String(option.value)] = option
  }
  return unique
}, {})).sort((left, right) => String(left.label).localeCompare(String(right.label)))

export const getFilterValueOptions = ({
  key,
  account,
  campaigns = [],
  adGroups = [],
  sourceTypes = [],
  marketingGroups = [],
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
} = {}) => {
  const accountId = Number(account?.id)
  const accountCampaigns = campaigns.filter(campaign => (
    Number(campaign?.account_id) === accountId
    && Number(campaign?.product_type_id) === Number(productTypeId)
    && !campaign?.discarded_at
  ))
  switch (key) {
    case "brand_ids":
      return uniqueFilterValueOptions((account?.brands || []).map(brand => ({ label: brand.name, value: brand.id })))
    case "campaign_ids":
      return uniqueFilterValueOptions(accountCampaigns.map(campaign => ({ label: campaign.name, value: campaign.id })))
    case "ad_group_ids":
      return uniqueFilterValueOptions(adGroups
        .filter(adGroup => accountCampaigns.some(campaign => Number(campaign.id) === Number(adGroup?.campaign_id)))
        .map(adGroup => ({ label: adGroup.ad_group_name || adGroup.name, value: adGroup.id })))
    case "states":
      return uniqueFilterValueOptions(us_states.map(state => ({ label: state.name, value: state.isocode })))
    case "lead_type_ids":
      return uniqueFilterValueOptions(INTEGRATION_LEAD_TYPES.map(leadType => ({ label: leadType.name, value: leadType.id })))
    case "source_type_ids":
      return uniqueFilterValueOptions(sourceTypes.map(sourceType => ({ label: sourceType.name, value: sourceType.id })))
    case "device_types":
      return uniqueFilterValueOptions(["Mobile", "Desktop", "Tablet", "Other"].map(deviceType => ({ label: deviceType, value: deviceType })))
    case "marketing_groups":
      return uniqueFilterValueOptions(marketingGroups.map(group => ({ label: group.name, value: group.name })))
    default:
      return []
  }
}

export const getSupportedGroupByOptions = ({ productTypeId = PRODUCT_TYPE_IDS.CLICKS, isFeatureFlagActive = () => false } = {}) => (
  SHARED_GROUP_BY_OPTIONS.filter(option => (
    (!option.products || option.products.includes(Number(productTypeId)))
    && (!option.featureFlag || isFeatureFlagActive(REQUIRED_FEATURE_FLAGS[option.featureFlag]))
    && [...(option.featureFlags || []), ...(option.featureFlagsByProduct?.[Number(productTypeId)] || [])]
      .every(flag => isFeatureFlagActive(REQUIRED_FEATURE_FLAGS[flag]))
  )).map(({ label, value }) => ({ label, value }))
)

export const parseFilterInput = (value, numeric = false) => String(value || "")
  .split(",")
  .map(item => item.trim())
  .filter(Boolean)
  .map(item => numeric ? Number(item) : item)
  .filter(item => numeric ? Number.isInteger(item) && item > 0 : true)

export const filtersFromRows = rows => REPORTING_CONSISTENCY_FILTER_OPTIONS.reduce((filters, option) => {
  const row = rows.find(filterRow => filterRow.key === option.key)
  const values = row ? parseFilterInput(row.values.map(value => value.value).join(","), option.numeric) : []
  filters[option.key] = values.length ? values : null
  return filters
}, {})

export const buildReportingConsistencyScope = ({
  accountId,
  selectedDates,
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  isGoalBrand = false,
  groupByParams = ["campaign_id"],
  filters = {},
  comparisonMode = REPORTING_COMPARISON_MODES.ALL_SURFACES,
} = {}) => {
  const dates = selectedDates?.start_date && selectedDates?.end_date ? selectedDates : lastMonth(REPORTING_TIMEZONE)
  const scope = {
    start_date: dates.display_start_date || String(dates.start_date).slice(0, 10),
    end_date: dates.display_end_date || String(dates.end_date).slice(0, 10),
    timezone: REPORTING_TIMEZONE,
    account_id: Number(accountId),
    account_ids: [Number(accountId)],
    product_type_ids: [Number(productTypeId)],
    product: getProductConfig(productTypeId, isGoalBrand).label,
    goal_brand: Boolean(isGoalBrand),
    comparison_mode: comparisonMode,
    group_by_params: groupByParams,
  }
  REPORTING_CONSISTENCY_FILTER_OPTIONS.forEach(({ key }) => {
    scope[key] = filters[key] || null
  })
  return scope
}

const isGoalMarketplaceProduct = (productTypeId, isGoalBrand = false) => (
  Boolean(isGoalBrand) && Number(productTypeId) === PRODUCT_TYPE_IDS.LEADS
)

const getProductConfig = (productTypeId, isGoalBrand = false) => (
  isGoalMarketplaceProduct(productTypeId, isGoalBrand)
    ? GOAL_LEAD_MARKETPLACE_CONFIG
    : REPORTING_PRODUCT_CONFIGS[productTypeId] || REPORTING_PRODUCT_CONFIGS[PRODUCT_TYPE_IDS.CLICKS]
)

const getMetricAliases = (productTypeId, source = "product", isGoalBrand = false) => (
  isGoalMarketplaceProduct(productTypeId, isGoalBrand) && source === "admin_v3"
    ? GOAL_ADMIN_ANALYTICS_V3_METRICS
    : REPORTING_SOURCE_METRICS[productTypeId]?.[source]
      || (source === "admin_v3"
        ? ADMIN_ANALYTICS_V3_METRICS[productTypeId]
        : getProductConfig(productTypeId, isGoalBrand).metrics)
)

const flagIsActive = (isFeatureFlagActive, flag) => isFeatureFlagActive?.(flag) === true

const accountFlagIsActive = (isFeatureFlagActiveForAccount, flag, account) => (
  Boolean(account)
  && account.internal !== true
  && isFeatureFlagActiveForAccount?.(flag, account) === true
)

const assertAccountScopedAdminAnalytics = ({ productTypeId, adminAnalyticsPath }) => {
  if (Number(productTypeId) === PRODUCT_TYPE_IDS.CLICKS && adminAnalyticsPath === "/api/v1/admin_analytics/clicks") {
    throw new Error("Reporting consistency is unavailable while the legacy Clicks Admin Analytics API is active. Enable the account-scoped Admin Analytics API first.")
  }
}

const resolveCampaignStatsVersion = ({ routing, featureFlagActive, useNewCampaignStats, useCampaignStatsV3 }) => {
  if (routing.featureFlagVersion && featureFlagActive) return routing.featureFlagVersion
  if (useNewCampaignStats && routing.newCampaignVersion) return routing.newCampaignVersion
  if (useCampaignStatsV3 && routing.accountFlagVersion) return routing.accountFlagVersion
  return routing.fallbackVersion
}

const resolveLegacyAdminAnalyticsVersion = ({ routing, isFeatureFlagActive }) => {
  if (routing.flag && flagIsActive(isFeatureFlagActive, routing.flag)) return routing.flagVersion
  return routing.fallbackVersion
}

const resolveDashboardVersion = ({ routing, isFeatureFlagActive, useDashboardV3 }) => {
  if (routing.featureFlagVersion && flagIsActive(isFeatureFlagActive, routing.featureFlag)) {
    return routing.featureFlagVersion
  }
  return useDashboardV3 ? routing.accountFlagVersion : routing.fallbackVersion
}

export const resolveReportingApiConfig = ({
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  account,
  isGoalBrand = false,
  isFeatureFlagActive = () => false,
  isFeatureFlagActiveForAccount = () => false,
} = {}) => {
  const productConfig = getProductConfig(productTypeId, isGoalBrand)
  const useNewCampaignStats = flagIsActive(
    isFeatureFlagActive,
    REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_NEW_CAMPAIGNS_PAGE_UI,
  )
  const campaignRouting = productConfig.routing.campaignStats
  const campaignFeatureFlagActive = flagIsActive(isFeatureFlagActive, campaignRouting.featureFlag)
  const useCampaignStatsV3 = accountFlagIsActive(
    isFeatureFlagActiveForAccount,
    campaignRouting.accountFlag,
    account,
  )
  const campaignStatsVersion = resolveCampaignStatsVersion({
    routing: campaignRouting,
    featureFlagActive: campaignFeatureFlagActive,
    useNewCampaignStats,
    useCampaignStatsV3,
  })
  const dashboardRouting = productConfig.routing.dashboard
  const useDashboardV3 = accountFlagIsActive(
    isFeatureFlagActiveForAccount,
    dashboardRouting.accountFlag,
    account,
  )
  const adminRouting = productConfig.routing.adminAnalytics
  const useAdminDashboardRevamp = flagIsActive(
    isFeatureFlagActive,
    REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_USE_ADMIN_DASHBOARD_REVAMP,
  )
  const adminAnalyticsPath = useAdminDashboardRevamp && adminRouting.revamp
    ? "/api/v3/admin_analytics/common"
    : `/api/${resolveLegacyAdminAnalyticsVersion({ routing: adminRouting, isFeatureFlagActive })}/admin_analytics/${productConfig.segment}`
  const dashboardVersion = resolveDashboardVersion({
    routing: dashboardRouting,
    isFeatureFlagActive,
    useDashboardV3,
  })
  const campaignStatsPath = productConfig.campaignStatsPathByVersion[campaignStatsVersion]
  const dashboardPath = productConfig.dashboardPathByVersion[dashboardVersion]

  if (!campaignStatsPath || !dashboardPath) {
    throw new Error(`No reporting API route is configured for ${productConfig.label}.`)
  }

  return {
    product: productConfig.label,
    adminAnalyticsPath,
    portalAnalyticsPath: productConfig.analyticsPath,
    campaignStatsPath,
    dashboardPath,
    campaignStatsVersion,
    dashboardVersion,
    childStatsVersion: productConfig.routing.childStats?.forceV4
      || (productConfig.routing.childStats
        && flagIsActive(isFeatureFlagActive, productConfig.routing.childStats.adGroupFeatureFlag)
        && flagIsActive(isFeatureFlagActive, productConfig.routing.childStats.sourceFeatureFlag))
      ? "v4"
      : "legacy",
  }
}

const REPORTING_PAGE_SIZE = 100
const REPORTING_CHILD_CONCURRENCY = 5

export const lastMonth = timezone => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "numeric",
  }).formatToParts(new Date())
  const year = Number(parts.find(part => part.type === "year").value)
  const month = Number(parts.find(part => part.type === "month").value)
  const start = new Date(Date.UTC(year, month - 2, 1))
  const end = new Date(Date.UTC(year, month - 1, 0))
  return {
    start_date: start.toISOString().slice(0, 10),
    end_date: end.toISOString().slice(0, 10),
  }
}

const reportingDate = value => value instanceof Date
  ? format(utcToZonedTime(value, REPORTING_TIMEZONE), "yyyy-MM-dd")
  : value

export const dateRangeToReportingDates = selectedDateRange => {
  const startDate = selectedDateRange?.value?.startDate
  const endDate = selectedDateRange?.value?.endDate
  return startDate && endDate
    ? {
      start_date: startDate instanceof Date ? startDate.toISOString() : startDate,
      end_date: endDate instanceof Date ? endDate.toISOString() : endDate,
      display_start_date: reportingDate(startDate),
      display_end_date: reportingDate(endDate),
    }
    : lastMonth(REPORTING_TIMEZONE)
}

export const requestJson = async (url, options = {}) => {
  const { query, returnRaw, returnHeaders, ...fetchOptions } = options
  const queryString = new URLSearchParams(
    Object.entries(query || {}).filter(([, value]) => value != null)
  ).toString()
  const requestUrl = queryString ? `${url}${url.includes("?") ? "&" : "?"}${queryString}` : url
  const response = await sf_fetch(requestUrl, fetchOptions)
  // responseErrorChecker throws a plain string for HTTP 500 responses before
  // this helper can attach the endpoint and status to the error. Read failed
  // responses directly so the consistency result can identify the request.
  const text = response.ok && !returnRaw ? await responseErrorChecker(response) : await response.text()
  let body
  try {
    body = text ? JSON.parse(text) : {}
  } catch (error) {
    body = { raw_text: text.slice(0, 500) }
  }
  if (!response.ok) {
    const detail = typeof body.error === "string"
      ? body.error
      : body.error?.message || body.message || body.errors?.title || body.raw_text || text || response.statusText || `HTTP ${response.status}`
    const requestError = new Error(`${options.method || "GET"} ${url} returned HTTP ${response.status}: ${detail}`)
    requestError.method = options.method || "GET"
    requestError.url = url
    requestError.status = response.status
    throw requestError
  }
  if (returnRaw) return { raw_text: text, headers: response.headers }
  return returnHeaders ? { body, headers: response.headers } : body
}

export const fetchAllReportingPages = async fetchPage => {
  const startedAt = Date.now()
  const rows = []
  let pageNumber = 1
  let requestCount = 0
  let complete = true

  while (true) {
    const response = await fetchPage(pageNumber)
    requestCount += 1
    rows.push(...reportingRows(response))

    if (Array.isArray(response)) break

    const pagination = response?.additional_data
    if (typeof pagination?.has_next_page === "boolean") {
      if (!pagination.has_next_page) break
      pageNumber += 1
      continue
    }

    complete = rows.length < REPORTING_PAGE_SIZE
    break
  }

  return {
    response: { data: rows },
    requestCount,
    complete,
    durationMs: Date.now() - startedAt,
  }
}

const fetchUnpaginatedReportingData = async fetchPage => {
  const startedAt = Date.now()
  const response = await fetchPage(1)
  return {
    response,
    requestCount: 1,
    complete: true,
    durationMs: Date.now() - startedAt,
  }
}

const mapWithConcurrency = async (items, concurrency, mapper) => {
  const results = new Array(items.length)
  let nextIndex = 0
  const workerCount = Math.min(concurrency, items.length)

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex
      nextIndex += 1
      results[index] = await mapper(items[index], index)
    }
  }

  await Promise.all(Array.from({ length: workerCount }, worker))
  return results
}

const reportingRequest = (path, { token, accountId, brandId, method = "GET", body, params } = {}) => {
  const query = params ? `?${new URLSearchParams(params).toString()}` : ""
  return requestJson(`${AppConfiguration.BACKEND_URL}${path}${query}`, {
    method,
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
      ...(accountId ? { "account-id": String(accountId) } : {}),
      ...(brandId ? { "brand-id": String(brandId) } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
}

const getCampaignId = row => row?.campaign_id ?? row?.id

const getReportingBrandId = (account, requestedBrandIds = []) => (
  requestedBrandIds[0] || account?.brands?.[0]?.id
)

const productRequiresBrand = (productTypeId, isGoalBrand = false) => (
  Number(productTypeId) === PRODUCT_TYPE_IDS.QUOTE_FUNNELS
  || isGoalMarketplaceProduct(productTypeId, isGoalBrand)
)

const normalizeDashboardResponse = (response, productTypeId) => {
  if (Number(productTypeId) !== PRODUCT_TYPE_IDS.QUOTE_FUNNELS) return response

  const totalStats = response?.data?.total_stats || response?.total_stats
  return { main_data: totalStats ? [totalStats] : [] }
}

const filterReportingResponseByCampaignIds = (payload, campaignIds) => ({
  data: reportingRows(payload).filter(row => campaignIds.has(String(getCampaignId(row)))),
})

const mergeCampaignChildResponses = (firstResponse, secondResponse) => {
  const firstData = firstResponse?.data && !Array.isArray(firstResponse.data) ? firstResponse.data : firstResponse
  const secondData = secondResponse?.data && !Array.isArray(secondResponse.data) ? secondResponse.data : secondResponse
  const mergedData = { ...firstData, ...secondData }
  return firstResponse?.data && !Array.isArray(firstResponse.data)
    ? { ...firstResponse, data: mergedData }
    : mergedData
}

export const resolveClientToken = async (accountId, adminToken) => {
  const client = bsh_getClientUserDetails()
  if (client?.token && (!client.account_id || String(client.account_id) === String(accountId))) {
    return client.token
  }

  const handoffResponse = await requestJson(`${AppConfiguration.BACKEND_URL}/api/v1/login`, {
    returnHeaders: true,
    method: "POST",
    headers: {
      Authorization: adminToken,
      "Admin-Token": adminToken,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ user: { account: Number(accountId) } }),
  })
  const handoff = handoffResponse.body
  const clientToken = handoffResponse.headers.get("Authorization") || handoff?.data?.token || handoff?.token
  if (!clientToken) {
    throw new Error(`Could not open account ${accountId} using the current admin session.`)
  }
  return clientToken
}

const runAllSurfacesReportingApis = async (
  accountId,
  selectedDates,
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  { account, isGoalBrand = false, isFeatureFlagActive, isFeatureFlagActiveForAccount } = {},
) => {
  const startedAt = Date.now()
  const timezone = "America/Los_Angeles"
  const dates = selectedDates?.start_date && selectedDates?.end_date ? selectedDates : lastMonth(timezone)
  const apiDates = { start_date: dates.start_date, end_date: dates.end_date }
  const displayDates = {
    start_date: dates.display_start_date || String(dates.start_date).slice(0, 10),
    end_date: dates.display_end_date || String(dates.end_date).slice(0, 10),
  }
  const productConfig = getProductConfig(productTypeId, isGoalBrand)
  const adminToken = bsh_getAdminUserDetails()?.token
  if (!adminToken) {
    throw new Error("The current admin session is missing. Sign in to the admin portal first.")
  }
  const apiConfig = resolveReportingApiConfig({
    productTypeId,
    account,
    isGoalBrand,
    isFeatureFlagActive,
    isFeatureFlagActiveForAccount,
  })
  assertAccountScopedAdminAnalytics({ productTypeId, adminAnalyticsPath: apiConfig.adminAnalyticsPath })
  const clientToken = await resolveClientToken(accountId, adminToken)
  const brandId = productRequiresBrand(productTypeId, isGoalBrand)
    ? getReportingBrandId(account)
    : undefined
  if (productRequiresBrand(productTypeId, isGoalBrand) && !brandId) {
    throw new Error(`${productConfig.label} checks require an account brand to select the brand-scoped reporting APIs.`)
  }

  const commonFilters = {
    ...apiDates,
    timezone,
    states: null,
    lead_type_ids: null,
    campaign_ids: null,
    ad_group_ids: null,
    // This is an account-level check. Do not inherit a potentially stale
    // current_brand value from a different account in browser storage.
    brand_ids: brandId ? [Number(brandId)] : null,
    marketing_groups: null,
    source_type_ids: null,
    device_types: null,
    account_manager_ids: null,
    sales_manager_ids: null,
    insurance_carrier_ids: null,
    invoice: null,
    group_by_params: ["campaign_id"],
  }
  // Keep the admin analytics route and optimization payload in lockstep with
  // the revamped Admin Analytics page. Its existing predicate controls the
  // use_admin_account_stats payload; the revamp flag controls the common API.
  const adminFiltersForRouting = {
    ...commonFilters,
    account_ids: [Number(accountId)],
    product_type_ids: [productTypeId],
  }
  const useAdminAccountStats = canUseAdminAccountStats(
    adminFiltersForRouting,
    commonFilters.group_by_params.map(value => ({ value })),
  )
  const adminPayload = {
    ...adminFiltersForRouting,
    ...(useAdminAccountStats ? { use_admin_account_stats: true } : {}),
  }
  const portalPayload = {
    ...commonFilters,
    days_of_week: null,
    individual_clicks: false,
    individual_calls: false,
    individual_leads: false,
    product_type_id: productTypeId,
  }
  const [adminAnalytics, portalAnalytics, campaignStats, dashboard, campaignCatalog] = await Promise.all([
    fetchAllReportingPages(pageNumber => reportingRequest(apiConfig.adminAnalyticsPath, {
      token: adminToken,
      method: "POST",
      body: { ...adminPayload, page_number: pageNumber, page_size: REPORTING_PAGE_SIZE },
    })),
    fetchAllReportingPages(pageNumber => reportingRequest(apiConfig.portalAnalyticsPath, {
      token: clientToken,
      accountId,
      brandId,
      method: "POST",
      body: { ...portalPayload, page_number: pageNumber, page_size: REPORTING_PAGE_SIZE },
    })),
    fetchUnpaginatedReportingData(() => reportingRequest(apiConfig.campaignStatsPath, {
      token: clientToken,
      accountId,
      brandId,
      params: apiDates,
    })),
    apiConfig.dashboardVersion === "v4"
      ? fetchUnpaginatedReportingData(() => reportingRequest(apiConfig.dashboardPath, {
        token: clientToken,
        accountId,
        brandId,
        params: productTypeId === PRODUCT_TYPE_IDS.QUOTE_FUNNELS
          ? { ...apiDates, stat_type: "overview" }
          : apiDates,
      })).then(collection => ({
        ...collection,
        response: normalizeDashboardResponse(collection.response, productTypeId),
      }))
      : fetchUnpaginatedReportingData(() => reportingRequest(apiConfig.dashboardPath, {
        token: clientToken,
        accountId,
        brandId,
        method: "POST",
        body: {
          main_start_date: apiDates.start_date,
          main_end_date: apiDates.end_date,
          brand_ids: brandId ? [Number(brandId)] : null,
          campaign_ids: null,
          states: null,
          lead_type_ids: null,
          compare_to_start_date: null,
          compare_to_end_date: null,
          product_type: productTypeId,
          ...(productTypeId === PRODUCT_TYPE_IDS.CLICKS ? { fetch_main_data: 1 } : {}),
        },
      })),
    fetchUnpaginatedReportingData(() => reportingRequest("/api/v1/admin_manage_clients/get_campaigns_by_account", {
      token: adminToken,
      method: "POST",
      body: {
        account_ids: [Number(accountId)],
        brand_ids: brandId ? [Number(brandId)] : null,
      },
    })),
  ])

  const selectedCampaignIds = new Set(reportingRows(campaignCatalog.response)
    .filter(row => Number(row.product_type_id) === Number(productTypeId))
    .map(getCampaignId)
    .filter(Boolean)
    .map(String))
  const adminResponse = filterReportingResponseByCampaignIds(adminAnalytics.response, selectedCampaignIds)
  const portalResponse = filterReportingResponseByCampaignIds(portalAnalytics.response, selectedCampaignIds)
  const campaignResponse = filterReportingResponseByCampaignIds(campaignStats.response, selectedCampaignIds)
  const campaignRows = reportingRows(campaignResponse).filter(row => row.campaign_id || row.id)
  const campaignChildStartedAt = Date.now()
  const splitCampaignChildStats = (
    productTypeId === PRODUCT_TYPE_IDS.CLICKS
    && apiConfig.campaignStatsPath === "/api/v3/click_campaign_stats"
  )
  const quoteFunnelV4ChildStats = (
    (productTypeId === PRODUCT_TYPE_IDS.QUOTE_FUNNELS || isGoalMarketplaceProduct(productTypeId, isGoalBrand))
    && apiConfig.campaignStatsVersion === "v4"
    && apiConfig.childStatsVersion === "v4"
  )
  const campaignChildren = await mapWithConcurrency(campaignRows, REPORTING_CHILD_CONCURRENCY, row => {
    const campaignId = row.campaign_id || row.id
    if (quoteFunnelV4ChildStats) {
      return Promise.all([
        reportingRequest(`/api/v4/campaigns/${campaignId}/ad_group_stats/${productConfig.segment}`, {
          token: clientToken,
          accountId,
          brandId,
          params: apiDates,
        }),
        reportingRequest(`/api/v4/campaigns/${campaignId}/source_stats/${productConfig.segment}`, {
          token: clientToken,
          accountId,
          brandId,
          params: apiDates,
        }),
      ]).then(([adGroups, sourceTypes]) => ({
        campaign_id: String(campaignId),
        response: {
          ad_groups: reportingRows(adGroups),
          campaign_source_settings: reportingRows(sourceTypes),
        },
        request_count: 2,
      }))
    }

    const childStatsPath = productTypeId === PRODUCT_TYPE_IDS.QUOTE_FUNNELS
      ? "/api/v2/click_campaign_stats"
      : apiConfig.campaignStatsPath
    const fetchChildStats = childParams => reportingRequest(`${apiConfig.campaignStatsPath}/${campaignId}`, {
      token: clientToken,
      accountId,
      brandId,
      params: { ...apiDates, ...childParams },
    })

    const fetchLegacyChildStats = childParams => reportingRequest(`${childStatsPath}/${campaignId}`, {
      token: clientToken,
      accountId,
      brandId,
      params: { ...apiDates, ...childParams },
    })

    if (!splitCampaignChildStats) {
      return (productTypeId === PRODUCT_TYPE_IDS.QUOTE_FUNNELS ? fetchLegacyChildStats : fetchChildStats)({ fetch_ad_groups: true, fetch_source_settings: true })
        .then(response => ({ campaign_id: String(campaignId), response, request_count: 1 }))
    }

    // The v3 Clicks show endpoint selects one child-stat collection at a
    // time, so request both collections separately and merge their payloads.
    return fetchChildStats({ fetch_ad_groups: true })
      .then(adGroupsResponse => fetchChildStats({ fetch_source_settings: true })
        .then(sourceSettingsResponse => ({
          campaign_id: String(campaignId),
          response: mergeCampaignChildResponses(adGroupsResponse, sourceSettingsResponse),
          request_count: 2,
        })))
  })
  const campaignChildRequestCount = campaignChildren.reduce(
    (sum, child) => sum + (child.request_count || 1),
    0,
  )
  const campaignChildDurationMs = Date.now() - campaignChildStartedAt
  const collectionComplete = [adminAnalytics, portalAnalytics, campaignStats].every(collection => collection.complete)

  return buildReportingConsistencyResult({
    scope: {
      ...commonFilters,
      start_date: displayDates.start_date,
      end_date: displayDates.end_date,
      account_id: Number(accountId),
      account_ids: [Number(accountId)],
      product_type_ids: [productTypeId],
      product: productConfig.label,
      goal_brand: Boolean(isGoalBrand),
      comparison_mode: REPORTING_COMPARISON_MODES.ALL_SURFACES,
      backend_url: AppConfiguration.BACKEND_URL,
      api_routes: {
        admin_analytics: apiConfig.adminAnalyticsPath,
        portal_analytics: apiConfig.portalAnalyticsPath,
        campaign_stats: apiConfig.campaignStatsPath,
        campaign_child_stats: `${apiConfig.campaignStatsPath}/:campaign_id`,
        dashboard: apiConfig.dashboardPath,
        campaign_catalog: "/api/v1/admin_manage_clients/get_campaigns_by_account",
      },
      api_route_timings: {
        admin_analytics: adminAnalytics.durationMs,
        portal_analytics: portalAnalytics.durationMs,
        campaign_stats: campaignStats.durationMs,
        campaign_child_stats: campaignChildDurationMs,
        dashboard: dashboard.durationMs,
        campaign_catalog: campaignCatalog.durationMs,
      },
    },
    adminResponse,
    portalResponse,
    campaignResponse,
    dashboardResponse: dashboard.response,
    campaignChildren,
    collectionComplete,
    requestCount: adminAnalytics.requestCount + portalAnalytics.requestCount + campaignStats.requestCount + dashboard.requestCount + campaignCatalog.requestCount + campaignChildRequestCount,
    durationMs: Date.now() - startedAt,
  })
}

const runAdminPortalReportingApis = async (
  accountId,
  selectedDates,
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  { account, isGoalBrand = false, isFeatureFlagActive, groupByParams = ["campaign_id"], filters = {} } = {},
) => {
  const startedAt = Date.now()
  const timezone = REPORTING_TIMEZONE
  const dates = selectedDates?.start_date && selectedDates?.end_date ? selectedDates : lastMonth(timezone)
  const apiDates = { start_date: dates.start_date, end_date: dates.end_date }
  const adminToken = bsh_getAdminUserDetails()?.token
  if (!adminToken) {
    throw new Error("The current admin session is missing. Sign in to the admin portal first.")
  }
  const apiConfig = resolveReportingApiConfig({ productTypeId, account, isGoalBrand, isFeatureFlagActive })
  assertAccountScopedAdminAnalytics({ productTypeId, adminAnalyticsPath: apiConfig.adminAnalyticsPath })
  const clientToken = await resolveClientToken(accountId, adminToken)
  const requestedBrandIds = filters.brand_ids?.length ? filters.brand_ids : []
  const requiresBrand = productRequiresBrand(productTypeId, isGoalBrand)
  if (requiresBrand && requestedBrandIds.length > 1) {
    throw new Error(`${getProductConfig(productTypeId, isGoalBrand).label} checks support one brand at a time because the reporting APIs are brand-scoped.`)
  }
  const brandId = requiresBrand
    ? getReportingBrandId(account, requestedBrandIds)
    : undefined
  if (requiresBrand && !brandId) {
    throw new Error(`${getProductConfig(productTypeId, isGoalBrand).label} checks require an account brand to select the brand-scoped reporting APIs.`)
  }
  const selectedFilters = REPORTING_CONSISTENCY_FILTER_OPTIONS.reduce((values, { key }) => ({
    ...values,
    [key]: key === "brand_ids" && brandId
      ? [Number(brandId)]
      : filters[key]?.length ? filters[key] : null,
  }), {})
  const commonFilters = {
    ...apiDates,
    timezone,
    ...selectedFilters,
    group_by_params: groupByParams,
  }
  const adminFiltersForRouting = {
    ...commonFilters,
    account_ids: [Number(accountId)],
    product_type_ids: [productTypeId],
  }
  const useAdminAccountStats = canUseAdminAccountStats(
    adminFiltersForRouting,
    groupByParams.map(value => ({ value })),
  )
  const adminPayload = {
    ...adminFiltersForRouting,
    ...(useAdminAccountStats ? { use_admin_account_stats: true } : {}),
  }
  const portalPayload = {
    ...commonFilters,
    days_of_week: null,
    individual_clicks: false,
    individual_calls: false,
    individual_leads: false,
    product_type_id: productTypeId,
  }
  const [adminAnalytics, portalAnalytics] = await Promise.all([
    fetchAllReportingPages(pageNumber => reportingRequest(apiConfig.adminAnalyticsPath, {
      token: adminToken,
      method: "POST",
      body: { ...adminPayload, page_number: pageNumber, page_size: REPORTING_PAGE_SIZE },
    })),
    fetchAllReportingPages(pageNumber => reportingRequest(apiConfig.portalAnalyticsPath, {
      token: clientToken,
      accountId,
      brandId,
      method: "POST",
      body: { ...portalPayload, page_number: pageNumber, page_size: REPORTING_PAGE_SIZE },
    })),
  ])
  const scope = {
    ...buildReportingConsistencyScope({
      accountId,
      selectedDates: dates,
      productTypeId,
      isGoalBrand,
      groupByParams,
      filters,
      comparisonMode: REPORTING_COMPARISON_MODES.ADMIN_PORTAL,
    }),
    backend_url: AppConfiguration.BACKEND_URL,
    api_routes: {
      admin_analytics: apiConfig.adminAnalyticsPath,
      portal_analytics: apiConfig.portalAnalyticsPath,
    },
    api_route_timings: {
      admin_analytics: adminAnalytics.durationMs,
      portal_analytics: portalAnalytics.durationMs,
    },
  }
  return buildAdminPortalReportingConsistencyResult({
    scope,
    adminResponse: adminAnalytics.response,
    portalResponse: portalAnalytics.response,
    collectionComplete: [adminAnalytics, portalAnalytics].every(collection => collection.complete),
    requestCount: adminAnalytics.requestCount + portalAnalytics.requestCount,
    durationMs: Date.now() - startedAt,
  })
}

export const runReportingApis = async (
  accountId,
  selectedDates,
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  options = {},
) => options.comparisonMode === REPORTING_COMPARISON_MODES.ADMIN_PORTAL
  ? runAdminPortalReportingApis(accountId, selectedDates, productTypeId, options)
  : runAllSurfacesReportingApis(accountId, selectedDates, productTypeId, options)

const reportingNumber = (row, aliases) => {
  const value = aliases.find(key => row[key] !== undefined && row[key] !== null)
  if (!value) return null
  const number = Number(String(row[value]).replace(/,/g, ""))
  return Number.isNaN(number) ? null : number
}

export const reportingRows = (payload, nestedKey) => {
  let value = payload
  if (value && !Array.isArray(value) && typeof value === "object" && value.data !== undefined) value = value.data
  if (value && !Array.isArray(value) && nestedKey) value = value[nestedKey] || []
  if (value && !Array.isArray(value) && typeof value === "object" && value.data !== undefined) value = value.data
  if (Array.isArray(value)) return value.filter(row => row && typeof row === "object")
  if (value && typeof value === "object") return [value]
  return []
}

const GROUP_BY_VALUE_ALIASES = {
  day_of_week: "dow",
  start_of_day: "date",
  start_of_week: "week",
  start_of_month: "month",
  start_of_quarter: "quarter",
  start_of_year: "year",
  currently_insured: "insured",
}

const groupByValue = (row, groupByParam) => row?.[groupByParam] ?? row?.[GROUP_BY_VALUE_ALIASES[groupByParam]] ?? null

const groupKey = (row, groupByParams) => JSON.stringify(
  groupByParams.map(groupByParam => [groupByParam, groupByValue(row, groupByParam) == null ? null : String(groupByValue(row, groupByParam))]),
)

const groupLabel = (row, groupByParams) => groupByParams.length
  ? groupByParams.map(groupByParam => `${displayName(groupByParam)}: ${groupByValue(row, groupByParam) ?? "(blank)"}`).join(" · ")
  : "Total"

export const normalizeReportingRow = (
  row,
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  source = "product",
  groupByParams = ["campaign_id"],
  isGoalBrand = false,
) => {
  const metricAliases = getMetricAliases(productTypeId, source, isGoalBrand)
  const campaignId = row?.campaign_id ?? row?.id
  const normalized = {
    campaign_id: campaignId === undefined || campaignId === null ? null : String(campaignId),
    comparison_key: groupKey(row, groupByParams),
    group_label: groupLabel(row, groupByParams),
  }
  Object.entries(metricAliases).forEach(([metric, aliases]) => {
    const value = reportingNumber(row || {}, aliases)
    if (value !== null) normalized[metric] = value
  })
  return normalized
}

const hasMetrics = (row, productTypeId = PRODUCT_TYPE_IDS.CLICKS, source = "product", isGoalBrand = false) => Object.keys(getMetricAliases(productTypeId, source, isGoalBrand))
  .some(metric => row[metric] !== undefined)

const comparableMetricNames = (productTypeId, source = "product", isGoalBrand = false) => {
  if (isGoalMarketplaceProduct(productTypeId, isGoalBrand) && GOAL_SOURCE_METRICS[source]) {
    return GOAL_SOURCE_METRICS[source]
  }
  return Object.keys(getMetricAliases(productTypeId, source, isGoalBrand))
}

const comparisonSource = sourceName => {
  if (sourceName === "admin_analytics") return "admin_v3"
  if (sourceName === "portal_analytics") return "portal"
  if (sourceName === "dashboard") return "dashboard"
  if (sourceName === "campaign_stats") return "campaign_stats"
  return "product"
}

const DERIVED_METRIC_REQUIREMENTS = {
  avg_bid: ["searches", "total_bid"],
  win_rate: ["searches", "impressions"],
  cpc: ["clicks", "cost"],
  success_rate: ["impressions", "successes"],
}

const comparableDerivedMetricNames = (productTypeId, leftSource, rightSource, isGoalBrand = false) => {
  const leftMetrics = comparableMetricNames(productTypeId, leftSource, isGoalBrand)
  const rightMetrics = comparableMetricNames(productTypeId, rightSource, isGoalBrand)
  return Object.entries(DERIVED_METRIC_REQUIREMENTS)
    .filter(([, requirements]) => requirements.every(metric => leftMetrics.includes(metric) && rightMetrics.includes(metric)))
    .map(([metric]) => metric)
}

const derivedMetrics = row => {
  const derived = {}
  if (row.searches) {
    if (row.total_bid !== undefined) derived.avg_bid = row.total_bid / row.searches
    if (row.impressions !== undefined) derived.win_rate = row.impressions / row.searches
  }
  if (row.clicks && row.cost !== undefined) derived.cpc = row.cost / row.clicks
  if (row.impressions && row.successes !== undefined) derived.success_rate = row.successes / row.impressions
  return derived
}

const compareMetricValues = (left, right, metrics, differences, tolerance, derived = false) => {
  metrics.forEach(metric => {
    const leftValue = left[metric]
    const rightValue = right[metric]
    if (leftValue === undefined || rightValue === undefined) {
      if (leftValue !== rightValue) {
        differences[metric] = {
          left: leftValue === undefined ? "Missing" : leftValue,
          right: rightValue === undefined ? "Missing" : rightValue,
          ...(derived ? { derived: true } : {}),
        }
      }
      return
    }
    if (Math.abs(leftValue - rightValue) > tolerance) {
      differences[metric] = { left: leftValue, right: rightValue, ...(derived ? { derived: true } : {}) }
    }
  })
}

const metricDifferences = (
  left,
  right,
  tolerance = 0.01,
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  leftSource = "product",
  rightSource = "product",
  isGoalBrand = false,
  metricsToCompare = null,
) => {
  const differences = {}
  const leftMetrics = comparableMetricNames(productTypeId, leftSource, isGoalBrand)
  const rightMetrics = comparableMetricNames(productTypeId, rightSource, isGoalBrand)
  const comparableMetrics = leftMetrics
    .filter(metric => rightMetrics.includes(metric))
    .filter(metric => !metricsToCompare || metricsToCompare.includes(metric))
  compareMetricValues(left, right, comparableMetrics, differences, tolerance)
  const leftDerived = derivedMetrics(left)
  const rightDerived = derivedMetrics(right)
  compareMetricValues(
    leftDerived,
    rightDerived,
    metricsToCompare
      ? comparableDerivedMetricNames(productTypeId, leftSource, rightSource, isGoalBrand)
        .filter(metric => metricsToCompare.includes(metric))
      : comparableDerivedMetricNames(productTypeId, leftSource, rightSource, isGoalBrand),
    differences,
    tolerance,
    true,
  )
  return differences
}

const compareReportingRows = (
  sources,
  tolerance = 0.01,
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  comparisonKeyField = "comparison_key",
  isGoalBrand = false,
  sourceMetrics = {},
) => {
  const bySource = Object.fromEntries(Object.entries(sources).map(([name, rows]) => [
    name,
    Object.fromEntries(rows
      .filter(row => row[comparisonKeyField] !== null && row[comparisonKeyField] !== undefined)
      .map(row => [String(row[comparisonKeyField]), row])),
  ]))
  const allComparisonIds = [...new Set(Object.values(bySource).flatMap(rows => Object.keys(rows)))].sort()
  const comparisons = allComparisonIds.map(comparisonId => {
    const rows = Object.fromEntries(Object.entries(bySource).map(([name, values]) => [name, values[comparisonId]]))
    const active = Object.fromEntries(Object.entries(rows).filter(([name, row]) => row && hasMetrics(row, productTypeId, sourceMetrics[name] || comparisonSource(name), isGoalBrand)))
    const noActivityIn = Object.entries(rows)
      .filter(([name, row]) => row && !hasMetrics(row, productTypeId, sourceMetrics[name] || comparisonSource(name), isGoalBrand))
      .map(([name]) => name)
      .sort()
    if (!Object.keys(active).length) {
      return {
        ...(comparisonKeyField === "comparison_key" ? { comparison_key: comparisonId } : {}),
        group_label: rows[Object.keys(rows).find(name => rows[name])]?.group_label || "Total",
        campaign_id: comparisonId,
        reference_source: null,
        present_in: [],
        missing_in: [],
        no_activity_in: noActivityIn,
        differences: {},
        status: "NO_ACTIVITY",
      }
    }
    const [referenceSource, reference] = Object.entries(active)[0]
    const differences = {}
    Object.entries(active).forEach(([name, row]) => {
      if (name !== referenceSource) {
        const rowDifferences = metricDifferences(
          reference,
          row,
          tolerance,
          productTypeId,
          sourceMetrics[referenceSource] || comparisonSource(referenceSource),
          sourceMetrics[name] || comparisonSource(name),
          isGoalBrand,
        )
        if (Object.keys(rowDifferences).length) differences[name] = rowDifferences
      }
    })
    const presentIn = Object.keys(active).sort()
    return {
      ...(comparisonKeyField === "comparison_key" ? { comparison_key: comparisonId } : {}),
      group_label: reference.group_label,
      campaign_id: comparisonId,
      reference_source: referenceSource,
      present_in: presentIn,
      missing_in: Object.keys(sources).filter(name => !presentIn.includes(name)).sort(),
      no_activity_in: noActivityIn,
      differences,
      status: !Object.keys(differences).length && presentIn.length === Object.keys(sources).length ? "PASS" : "DATA_MISMATCH",
    }
  })
  return {
    status: comparisons.every(item => ["PASS", "NO_ACTIVITY"].includes(item.status)) ? "PASS" : "DATA_MISMATCH",
    rows_checked: comparisons.length,
    comparisons,
  }
}

const sumReportingRows = (
  rows,
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  isGoalBrand = false,
  source = "product",
) => Object.fromEntries(
  (getProductConfig(productTypeId, isGoalBrand).aggregationMetrics || Object.keys(getMetricAliases(productTypeId, source, isGoalBrand)))
    .filter(metric => rows.some(row => row[metric] !== undefined))
    .map(metric => [metric, rows.filter(row => row[metric] !== undefined).reduce((sum, row) => sum + row[metric], 0)])
)

const hasMetricActivity = (row, productTypeId, source, isGoalBrand = false) => comparableMetricNames(productTypeId, source, isGoalBrand)
  .some(metric => row?.[metric] !== undefined && Math.abs(row[metric]) > 0.01)

const buildAdminPortalReportingConsistencyResult = ({
  scope,
  adminResponse,
  portalResponse,
  collectionComplete = true,
  requestCount,
  durationMs,
}) => {
  const productTypeId = scope?.product_type_ids?.[0] || PRODUCT_TYPE_IDS.CLICKS
  const isGoalBrand = scope?.goal_brand === true
  const productConfig = getProductConfig(productTypeId, isGoalBrand)
  const isAdminAnalyticsV3 = scope?.api_routes?.admin_analytics === "/api/v3/admin_analytics/common"
  const groupByParams = scope?.group_by_params || ["campaign_id"]
  const adminRows = reportingRows(adminResponse).map(row => normalizeReportingRow(
    row,
    productTypeId,
    isAdminAnalyticsV3 ? "admin_v3" : "product",
    groupByParams,
    isGoalBrand,
  ))
  const portalRows = reportingRows(portalResponse).map(row => normalizeReportingRow(row, productTypeId, "portal", groupByParams, isGoalBrand))
  const surfaces = compareReportingRows({
    admin_analytics: adminRows,
    portal_analytics: portalRows,
  }, 0.01, productTypeId, "comparison_key", isGoalBrand, {
    admin_analytics: isAdminAnalyticsV3 ? "admin_v3" : "product",
  })
  const consistencyOk = collectionComplete && surfaces.status === "PASS"
  const accountId = (scope.account_ids || [scope.account_id])[0]
  const classification = !collectionComplete ? "INCOMPLETE" : consistencyOk ? "PASS" : "DATA_MISMATCH"
  return {
    ok: true,
    consistency_ok: consistencyOk,
    classification,
    collection_complete: collectionComplete,
    scope,
    surfaces,
    request_count: requestCount,
    duration_ms: durationMs,
    visible_summary: collectionComplete
      ? `Compared ${productConfig.label} Admin Analytics and Portal Analytics for account ${accountId}.`
      : `The run was incomplete because a full reporting page did not include pagination metadata. Results cover the returned data only for account ${accountId}.`,
  }
}

const buildAllSurfacesReportingConsistencyResult = ({
  scope,
  adminResponse,
  portalResponse,
  campaignResponse,
  dashboardResponse,
  campaignChildren,
  collectionComplete = true,
  requestCount,
  durationMs,
}) => {
  const productTypeId = scope?.product_type_ids?.[0] || PRODUCT_TYPE_IDS.CLICKS
  const isGoalBrand = scope?.goal_brand === true
  const productConfig = getProductConfig(productTypeId, isGoalBrand)
  const isAdminAnalyticsV3 = scope?.api_routes?.admin_analytics === "/api/v3/admin_analytics/common"
  const adminRows = reportingRows(adminResponse).map(row => normalizeReportingRow(
    row,
    productTypeId,
    isAdminAnalyticsV3 ? "admin_v3" : "product",
    ["campaign_id"],
    isGoalBrand,
  ))
  const portalRows = reportingRows(portalResponse).map(row => normalizeReportingRow(row, productTypeId, "portal", ["campaign_id"], isGoalBrand))
  const campaignRows = reportingRows(campaignResponse)
    .map(row => normalizeReportingRow(row, productTypeId, "campaign_stats", ["campaign_id"], isGoalBrand))
    .filter(row => row.campaign_id)
  const surfaces = compareReportingRows({
    admin_analytics: adminRows,
    portal_analytics: portalRows,
    campaign_stats: campaignRows,
  }, 0.01, productTypeId, "campaign_id", isGoalBrand, {
    admin_analytics: isAdminAnalyticsV3 ? "admin_v3" : "product",
  })
  const dashboardRows = reportingRows(dashboardResponse, "main_data")
  const dashboardTotal = dashboardRows.length ? normalizeReportingRow(dashboardRows[0], productTypeId, "dashboard", ["campaign_id"], isGoalBrand) : {}
  const aggregationMetrics = productConfig.aggregationMetrics || Object.keys(productConfig.metrics)
  const campaignTotal = sumReportingRows(campaignRows, productTypeId, isGoalBrand, "campaign_stats")
  const hasDashboardRollupActivity = [
    { row: dashboardTotal, source: "dashboard" },
    { row: campaignTotal, source: "campaign_stats" },
    ...adminRows.map(row => ({ row, source: isAdminAnalyticsV3 ? "admin_v3" : "product" })),
    ...portalRows.map(row => ({ row, source: "portal" })),
  ].some(({ row, source }) => hasMetricActivity(row, productTypeId, source, isGoalBrand))
  const dashboardDifferences = hasDashboardRollupActivity
    ? metricDifferences(dashboardTotal, campaignTotal, 0.01, productTypeId, "dashboard", "campaign_stats", isGoalBrand, aggregationMetrics)
    : {}
  const campaignById = Object.fromEntries(campaignRows.map(row => [row.campaign_id, row]))
  const returnedCampaignIds = new Set()
  const campaigns = campaignChildren
    .slice()
    .sort((left, right) => String(left.campaign_id).localeCompare(String(right.campaign_id)))
    .flatMap(child => {
      const campaignId = String(child.campaign_id)
      if (!campaignById[campaignId]) return []
      returnedCampaignIds.add(campaignId)
      const detailResponse = child.response || child
      const adGroups = reportingRows(detailResponse, "ad_groups").map(row => normalizeReportingRow(row, productTypeId, "campaign_stats", ["campaign_id"], isGoalBrand))
      const sourceTypes = reportingRows(detailResponse, "campaign_source_settings").map(row => normalizeReportingRow(row, productTypeId, "campaign_stats", ["campaign_id"], isGoalBrand))
      const campaign = campaignById[campaignId]
      if (!hasMetrics(campaign, productTypeId, "campaign_stats", isGoalBrand)) {
        return [{
          campaign_id: campaignId,
          ad_group_count: adGroups.length,
          source_type_count: sourceTypes.length,
          status: "NO_ACTIVITY",
          ad_groups: { status: "NO_ACTIVITY", differences: {} },
          source_types: { status: "NO_ACTIVITY", differences: {} },
        }]
      }
      const adGroupDifferences = metricDifferences(campaign, sumReportingRows(adGroups, productTypeId, isGoalBrand, "campaign_stats"), 0.01, productTypeId, "campaign_stats", "campaign_stats", isGoalBrand, aggregationMetrics)
      const sourceDifferences = metricDifferences(campaign, sumReportingRows(sourceTypes, productTypeId, isGoalBrand, "campaign_stats"), 0.01, productTypeId, "campaign_stats", "campaign_stats", isGoalBrand, aggregationMetrics)
      return [{
        campaign_id: campaignId,
        ad_group_count: adGroups.length,
        source_type_count: sourceTypes.length,
        ad_groups: { status: Object.keys(adGroupDifferences).length ? "DATA_MISMATCH" : "PASS", differences: adGroupDifferences },
        source_types: { status: Object.keys(sourceDifferences).length ? "DATA_MISMATCH" : "PASS", differences: sourceDifferences },
      }]
    })
  Object.keys(campaignById).filter(campaignId => !returnedCampaignIds.has(campaignId)).sort().forEach(campaignId => {
    campaigns.push({
      campaign_id: campaignId,
      ad_group_count: 0,
      source_type_count: 0,
      ad_groups: { status: "DATA_MISMATCH", differences: { missing: true } },
      source_types: { status: "DATA_MISMATCH", differences: { missing: true } },
    })
  })
  const childFailed = campaigns.some(item => !["PASS", "NO_ACTIVITY"].includes(item.ad_groups.status) || !["PASS", "NO_ACTIVITY"].includes(item.source_types.status))
  const consistencyOk = collectionComplete && surfaces.status === "PASS" && !Object.keys(dashboardDifferences).length && !childFailed
  const accountId = (scope.account_ids || [scope.account_id])[0]
  const classification = !collectionComplete ? "INCOMPLETE" : consistencyOk ? "PASS" : "DATA_MISMATCH"
  return {
    ok: true,
    consistency_ok: consistencyOk,
    classification,
    collection_complete: collectionComplete,
    scope,
    surfaces,
    dashboard_rollup: {
      status: Object.keys(dashboardDifferences).length ? "DATA_MISMATCH" : "PASS",
      differences: dashboardDifferences,
    },
    campaign_children: {
      status: childFailed ? "DATA_MISMATCH" : "PASS",
      campaigns,
    },
    request_count: requestCount,
    duration_ms: durationMs,
    visible_summary: collectionComplete
      ? `Compared ${productConfig.label} Admin Analytics, Portal Analytics, Campaign stats, Dashboard rollup, and ${campaigns.length} campaign child-stat sets for account ${accountId}.`
      : `The run was incomplete because a full reporting page did not include pagination metadata. Results cover the returned data only for account ${accountId}.`,
  }
}

export const buildReportingConsistencyResult = args => args?.scope?.comparison_mode === REPORTING_COMPARISON_MODES.ADMIN_PORTAL
  ? buildAdminPortalReportingConsistencyResult(args)
  : buildAllSurfacesReportingConsistencyResult(args)

export const createReportingErrorResult = ({
  accountId,
  dates,
  error,
  productTypeId = PRODUCT_TYPE_IDS.CLICKS,
  isGoalBrand = false,
  scope = {},
}) => {
  const status = error?.status || null
  const message = typeof error === "string"
    ? error
    : error?.message || "The reporting request failed before the comparison could complete."
  const method = error?.method || "GET"
  const endpoint = error?.url || "Reporting API"
  return {
    ok: false,
    consistency_ok: false,
    classification: "API_ERROR",
    collection_complete: false,
    scope: {
      ...scope,
      account_id: Number(accountId),
      account_ids: [Number(accountId)],
      start_date: dates?.start_date,
      end_date: dates?.end_date,
      timezone: "America/Los_Angeles",
      product_type_ids: [productTypeId],
      product: getProductConfig(productTypeId, isGoalBrand).label,
      goal_brand: Boolean(isGoalBrand),
      backend_url: AppConfiguration.BACKEND_URL,
    },
    error: { status, method, endpoint, message },
    request_count: 0,
    duration_ms: 0,
    visible_summary: status
      ? `The consistency check stopped because an API request returned HTTP ${status}.`
      : "The consistency check stopped because an API request failed.",
  }
}

export const resultStatus = result => {
  if (result?.consistency_ok === true) return "PASS"
  if (result?.classification) return String(result.classification).replace(/_/g, " ")
  return "ERROR"
}

export const createRunEntry = result => ({
  id: Date.now(),
  status: resultStatus(result),
  result,
  started_at: new Date().toISOString(),
  request_count: result?.request_count,
})

export const comparisonStatus = comparison => {
  if (comparison?.status === "PASS") return "PASS"
  if (comparison?.status === "NO_ACTIVITY") return "NO ACTIVITY"
  if (comparison?.missing_in?.length) return "MISSING DATA"
  return "MISMATCH"
}

export const comparisonSourceStatus = (comparison, source) => {
  if (comparison?.present_in?.includes(source)) return "Matched"
  if (comparison?.no_activity_in?.includes(source)) return "No activity"
  return "Not found"
}

export const displayName = value => String(value || "").replace(/_/g, " ").replace(/\b\w/g, character => character.toUpperCase())

export const flattenDifferenceRows = (differences, prefix = "") => Object.entries(differences || {}).flatMap(([key, value]) => {
  const check = prefix ? `${prefix} · ${displayName(key)}` : displayName(key)
  if (value && typeof value === "object" && ("left" in value || "right" in value)) {
    return [{ check, expected: value.left ?? "-", actual: value.right ?? "-" }]
  }
  if (value && typeof value === "object" && "missing" in value) {
    return [{ check, expected: "Present", actual: "Missing" }]
  }
  if (value && typeof value === "object") return flattenDifferenceRows(value, check)
  return [{ check, expected: "-", actual: value ?? "-" }]
})

const addFailureDetails = (failures, differences, location, expectedLabel, actualLabel) => {
  flattenDifferenceRows(differences).forEach(difference => {
    failures.push({
      metric: `${difference.check} mismatch`,
      location,
      expectedLabel,
      actualLabel,
      expected: difference.expected,
      actual: difference.actual,
    })
  })
}

export const failureDetailsForResult = result => {
  const failures = []
  if (["API_ERROR", "ERROR"].includes(result?.classification) || result?.error) {
    const apiError = result.error || {}
    const status = apiError.status ? ` (${apiError.status})` : ""
    failures.push({
      metric: `API error${status}`,
      location: `${apiError.method || "GET"} ${apiError.endpoint || "Reporting API"}`,
      expectedLabel: "Expected",
      actualLabel: "Received",
      expected: "Successful response",
      actual: apiError.status ? `HTTP ${apiError.status}: ${apiError.message}` : apiError.message || "Request failed",
    })
    return failures
  }
  const comparisons = result?.surfaces?.comparisons || []

  comparisons.forEach(comparison => {
    if (comparison.status !== "DATA_MISMATCH") return
    const campaignLabel = comparison.group_label || `Campaign ${comparison.campaign_id}`
    comparison.missing_in?.forEach(source => {
      failures.push({
        metric: "Reporting data missing",
        location: `${campaignLabel} · ${displayName(source)}`,
        expectedLabel: "Other surfaces",
        actualLabel: displayName(source),
        expected: "Present",
        actual: "Missing",
      })
    })
    Object.entries(comparison.differences || {}).forEach(([source, differences]) => {
      addFailureDetails(
        failures,
        differences,
        `${campaignLabel} · ${displayName(comparison.reference_source)} vs ${displayName(source)}`,
        displayName(comparison.reference_source),
        displayName(source),
      )
    })
  })

  addFailureDetails(
    failures,
    result?.dashboard_rollup?.differences,
    "Dashboard vs Campaign total",
    "Dashboard",
    "Campaign total",
  )

  const campaigns = result?.campaign_children?.campaigns || []
  campaigns.forEach(campaign => {
    [["Ad groups", campaign.ad_groups], ["Sources", campaign.source_types]].forEach(([childLabel, childResult]) => {
      if (childResult?.status !== "DATA_MISMATCH") return
      addFailureDetails(
        failures,
        childResult.differences,
        `Campaign ${campaign.campaign_id} · Campaign total vs ${childLabel}`,
        "Campaign total",
        `${childLabel} total`,
      )
    })
  })

  if (!failures.length && result?.classification === "INCOMPLETE") {
    failures.push({
      metric: "Check incomplete",
      location: "Reporting collection",
      expectedLabel: "Expected",
      actualLabel: "Actual",
      expected: "Complete response",
      actual: "Incomplete response",
    })
  }

  return failures
}

export const groupFailureDetails = failures => Object.values(failures.reduce((groups, failure) => {
  const key = `${failure.location}|${failure.expectedLabel}|${failure.actualLabel}`
  if (!groups[key]) {
    groups[key] = {
      location: failure.location,
      expectedLabel: failure.expectedLabel,
      actualLabel: failure.actualLabel,
      values: [],
    }
  }
  groups[key].values.push(failure)
  return groups
}, {}))

export const formatInvestigationValue = value => {
  if (value === undefined || value === null) return "Missing"
  if (typeof value === "number") return String(value)
  return String(value)
}

export const createInvestigationReport = result => {
  const scope = result?.scope || {}
  const isAllSurfaces = scope.comparison_mode !== REPORTING_COMPARISON_MODES.ADMIN_PORTAL
  const failures = failureDetailsForResult(result)
  const failureGroups = groupFailureDetails(failures)
  const routes = scope.api_routes || {}
  const routeTiming = route => scope.api_route_timings?.[route] != null
    ? ` - ${scope.api_route_timings[route]} ms`
    : ""
  const lines = [
    "REPORTING CONSISTENCY INVESTIGATION",
    `Account: ${formatInvestigationValue(scope.account_id)}`,
    `Product: ${formatInvestigationValue(scope.product)}`,
    `Period: ${formatInvestigationValue(scope.start_date)} to ${formatInvestigationValue(scope.end_date)}`,
    `Timezone: ${formatInvestigationValue(scope.timezone)}`,
    `Grouped by: ${(scope.group_by_params || []).join(", ") || "-"}`,
    `Classification: ${formatInvestigationValue(result?.classification)}`,
    `Collection complete: ${formatInvestigationValue(result?.collection_complete)}`,
    `Requests: ${formatInvestigationValue(result?.request_count)}`,
    `Duration: ${result?.duration_ms != null ? `${result.duration_ms} ms` : "-"}`,
    `Backend: ${formatInvestigationValue(scope.backend_url)}`,
    "",
    "API ROUTES",
    `Admin Analytics: POST ${formatInvestigationValue(routes.admin_analytics)}${routeTiming("admin_analytics")}`,
    `Portal Analytics: POST ${formatInvestigationValue(routes.portal_analytics)}${routeTiming("portal_analytics")}`,
    ...(isAllSurfaces ? [
      `Campaign stats collection: GET ${formatInvestigationValue(routes.campaign_stats)}${routeTiming("campaign_stats")}`,
      `Campaign child stats: GET ${formatInvestigationValue(routes.campaign_child_stats)}${routeTiming("campaign_child_stats")}`,
      `Campaign catalog: POST ${formatInvestigationValue(routes.campaign_catalog)}${routeTiming("campaign_catalog")}`,
      `Dashboard: POST ${formatInvestigationValue(routes.dashboard)}${routeTiming("dashboard")}`,
    ] : []),
    "",
    `FINDINGS (${failures.length} metrics across ${failureGroups.length} comparisons)`,
  ]

  if (!failures.length) {
    lines.push("No mismatches or API errors were found.")
  } else {
    failureGroups.forEach((group, index) => {
      const values = group.values.map(failure => (
        `${failure.metric.replace(/ mismatch$/, "")}: ${group.expectedLabel}=${formatInvestigationValue(failure.expected)}, ${group.actualLabel}=${formatInvestigationValue(failure.actual)}`
      )).join("; ")
      lines.push(`${index + 1}. ${group.location} | ${values}`)
    })
  }

  return lines.join("\n")
}

export const copyTextToClipboard = async text => {
  const copyBuffer = document.createElement("textarea")
  copyBuffer.className = "reporting-consistency__copy-buffer"
  copyBuffer.value = text
  document.body.appendChild(copyBuffer)
  copyBuffer.select()
  let copied = false
  try {
    copied = document.execCommand("copy")
  } finally {
    document.body.removeChild(copyBuffer)
  }
  if (copied) return
  if (navigator.clipboard?.writeText) {
    await Promise.race([
      navigator.clipboard.writeText(text),
      new Promise((resolve, reject) => window.setTimeout(() => reject(new Error("Clipboard access timed out")), 1000)),
    ])
    return
  }
  throw new Error("Clipboard access is unavailable")
}

export const statusPresentation = status => {
  const normalized = String(status || "UNKNOWN").toUpperCase().replace(/_/g, " ")
  if (normalized === "PASS" || normalized === "MATCHED") {
    return { label: normalized, icon: "bx-check-circle", className: "text-success" }
  }
  if (normalized === "NO ACTIVITY" || normalized === "NO ACTIVITY IN SELECTED PERIOD") {
    return { label: normalized, icon: "bx-minus-circle", className: "text-warning" }
  }
  if (normalized === "MISMATCH" || normalized === "MISSING DATA" || normalized === "DATA MISMATCH" || normalized === "ERROR" || normalized === "API ERROR") {
    return { label: normalized, icon: "bx-error-circle", className: "text-danger" }
  }
  if (normalized === "INCOMPLETE") {
    return { label: normalized, icon: "bx-error-circle", className: "text-warning" }
  }
  return { label: normalized, icon: "bx-question-mark", className: "text-muted" }
}
